// ✅ StoreAdmin.js (Chunk 1 of 3)
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useSchool } from "../hooks/useSchool";
import { QRCodeSVG } from "qrcode.react";

const storage = getStorage();

const StoreAdmin = () => {
  const schoolId = useSchool();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [ticketCost, setTicketCost] = useState(0);
  const [imageUrl, setImageUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [defaultCost, setDefaultCost] = useState(1);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [printOpen, setPrintOpen] = useState(false);
  const [printIds, setPrintIds] = useState([]);
  const [includePrintImages, setIncludePrintImages] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [description, setDescription] = useState("");

  const baseOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const itemQR = (id) =>
    schoolId
      ? `${baseOrigin}/${schoolId}/store-mode?item=${id}`
      : `${baseOrigin}/store-mode?item=${id}`;

      const loadItems = async () => {
        setLoading(true);
        try {
          const col = schoolId
            ? collection(db, "schools", schoolId, "store")
            : collection(db, "store");
      
          console.log("StoreAdmin loadItems -> schoolId:", schoolId, "collection path:", col.path);
          console.log("Firebase projectId:", db.app.options.projectId);
      
          const snap = await getDocs(col);
          setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } finally {
          setLoading(false);
        }
      };

  useEffect(() => {
    loadItems();
  }, [schoolId]);

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const cost = Number(ticketCost) || 0;

    const storeCol = schoolId
      ? collection(db, "schools", schoolId, "store")
      : collection(db, "store");

    await addDoc(storeCol, {
      name: name.trim(),
      ticketCost: cost,
      imageUrl: imageUrl.trim(),
      available: true,
      createdAt: serverTimestamp(),
      ...(schoolId ? { schoolId } : {}),
    });

    setName("");
    setTicketCost(0);
    setImageUrl("");
    loadItems();
  };
    // ---- actions ----
    const toggleAvailable = async (id, current) => {
      const docRef = schoolId
        ? doc(db, "schools", schoolId, "store", id)
        : doc(db, "store", id);
      await updateDoc(docRef, { available: !current });
      loadItems();
    };
  
    const handleDelete = async (id) => {
      if (!window.confirm("Delete this item?")) return;
      const docRef = schoolId
        ? doc(db, "schools", schoolId, "store", id)
        : doc(db, "store", id);
      await deleteDoc(docRef);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setSelected((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    };
  
    // ---- filters & selection ----
    const filtered = useMemo(() => {
      const s = search.toLowerCase();
      return items
        .filter((i) => (i.name || "").toLowerCase().includes(s))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }, [items, search]);
  
    const anySelected = useMemo(
      () => Object.values(selected).some(Boolean),
      [selected]
    );
  
    const selectedIds = useMemo(
      () => Object.entries(selected).filter(([, v]) => v).map(([k]) => k),
      [selected]
    );
  
    const toggleSelect = (id) =>
      setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  
    const selectAllFiltered = () => {
      const map = { ...selected };
      filtered.forEach((i) => (map[i.id] = true));
      setSelected(map);
    };
  
    const clearSelection = () => setSelected({});
  
    // ---- PRINT SHEET (no new tab, no sign-in) ----
    const openPrintSheet = (ids) => {
      if (!ids || ids.length === 0) return;
      setPrintIds(ids);
      setPrintOpen(true);
    };
    const closePrintSheet = () => {
      setPrintOpen(false);
      // Do NOT clear printIds right away – preserve it for print-only fallback
      // We'll let the user manually refresh or trigger selection again if needed
    };
  
    // ---- DRAG & DROP UPLOAD ----
    const onDropFiles = async (files) => {
      if (!files || files.length === 0) return;
      setUploadBusy(true);
      try {
        await Promise.all(
          files.map(async (file) => {
            const ts = Date.now();
            const safeName = file.name.replace(/\s+/g, "_");
            const storagePath = schoolId
              ? `schools/${schoolId}/store-images/${ts}-${safeName}`
              : `store-images/${ts}-${safeName}`;
            const r = ref(storage, storagePath);
            await uploadBytes(r, file);
            const url = await getDownloadURL(r);
    
            const derivedName = (file.name || "New Item")
              .replace(/\.[^.]+$/, "")
              .replace(/[_-]+/g, " ");
    
            const storeRef = schoolId
              ? collection(db, "schools", schoolId, "store")
              : collection(db, "store");
    
            await addDoc(storeRef, {
              name: derivedName,
              ticketCost: Number(defaultCost) || 0,
              imageUrl: url,
              available: true,
              createdAt: serverTimestamp(),
              ...(schoolId ? { schoolId } : {}),
            });
          })
        );
    
        await loadItems();
      } catch (e) {
        console.error("Upload failed:", e);
        alert("One or more uploads failed. Check console for details.");
      } finally {
        setUploadBusy(false);
      }
    };
  
    const handleDrop = async (e) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files || []).filter((f) =>
        f.type.startsWith("image/")
      );
      await onDropFiles(files);
    };
  
    const handleFilePicker = async (e) => {
      const files = Array.from(e.target.files || []).filter((f) =>
        f.type.startsWith("image/")
      );
      await onDropFiles(files);
      e.target.value = "";
    };
  
    // --- small UI helpers ---
    const Button = ({ children, onClick, variant = "primary", ...rest }) => {
      const base =
        "px-3 py-2 rounded text-sm font-medium border inline-flex items-center gap-1";
      const styles = {
        primary:
          base + " bg-blue-600 text-white border-blue-600 hover:opacity-90",
        green:
          base + " bg-green-600 text-white border-green-600 hover:opacity-90",
        red: base + " bg-red-600 text-white border-red-600 hover:opacity-90",
        gray:
          base + " bg-gray-100 text-gray-800 border-gray-300 hover:bg-gray-200",
        indigo:
          base + " bg-indigo-600 text-white border-indigo-600 hover:opacity-90",
        outline: base + " bg-white text-gray-800 border-gray-300 hover:bg-gray-50",
      };
      return (
        <button onClick={onClick} className={styles[variant]} {...rest}>
          {children}
        </button>
      );
    };
    return (
      <div className="dashboard-container">
        {/* Header row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "1rem",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <h2 className="section-title">🛍 Store Manager</h2>
  
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <Button
              variant="indigo"
              onClick={() => openPrintSheet(selectedIds)}
              disabled={!anySelected}
              title={anySelected ? "Print QR labels for selected items" : "Select items first"}
            >
              🖨️ Print Selected Labels
            </Button>
  
            <Button variant="gray" onClick={selectAllFiltered} disabled={filtered.length === 0}>
              Select All (shown)
            </Button>
            <Button variant="gray" onClick={clearSelection}>
              Clear Selection
            </Button>
          </div>
        </div>
  
        {!schoolId && (
          <div
            style={{
              background: "#fff7ed",
              border: "1px solid #fdba74",
              color: "#9a3412",
              padding: "0.5rem 0.75rem",
              borderRadius: 8,
              marginTop: "0.75rem",
            }}
          >
            ⚠ Tip: use your school URL (e.g. <code>/nlcs/store-admin</code>) so items are created with the correct <code>schoolId</code>.
          </div>
        )}
  
       {/* Unified Item Upload Section */}
<div className="section-card" style={{ marginTop: "1rem" }}>
  <h3 style={{ marginBottom: 8 }}>📦 Add or Upload Store Item</h3>

  <form
onSubmit={async (e) => {
  e.preventDefault();
  if (!name.trim() || !fileToUpload) return;

  setUploadBusy(true);
  setSuccessMessage("");
  try {
    const ts = Date.now();
    const safeName = fileToUpload.name.replace(/\s+/g, "_");
    const storagePath = schoolId
      ? `schools/${schoolId}/store-images/${ts}-${safeName}`
      : `store-images/${ts}-${safeName}`;

    const r = ref(storage, storagePath);
    await uploadBytes(r, fileToUpload, {
      contentType: fileToUpload.type || "image/jpeg",
    });

    const url = await getDownloadURL(r);

    const storeRef = schoolId
      ? collection(db, "schools", schoolId, "store")
      : collection(db, "store");

    const newDoc = await addDoc(storeRef, {
      name: name.trim(),
      description: description.trim(),
      ticketCost: Number(ticketCost) || 0,
      imageUrl: url,
      available: true,
      createdAt: serverTimestamp(),
      ...(schoolId ? { schoolId } : {}),
    });

    console.log("NEW STORE ITEM CREATED AT:", newDoc.path);
    console.log("schoolId used:", schoolId);
    console.log("storeRef path:", storeRef.path);

        setName("");
        setDescription("");
        setTicketCost(0);
        setFileToUpload(null);
        setSuccessMessage("✅ Item added successfully!");
        await loadItems();
      } catch (err) {
        console.error("Upload failed:", err);
        alert("Upload failed. Check console for details.");
      } finally {
        setUploadBusy(false);
      }
    }}
    style={{
      display: "grid",
      gap: "1rem",
      gridTemplateColumns: "1fr",
      alignItems: "start",
    }}
  >
    {/* Name and Description */}
    <div style={{ display: "grid", gap: "0.5rem" }}>
      <input
        className="border p-2"
        placeholder="Item name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
      />
      <textarea
        className="border p-2"
        placeholder="Item description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
      />
    </div>

    {/* Ticket Cost */}
    <div>
      <label style={{ fontSize: 14, color: "#374151", marginRight: 8 }}>
        Ticket Value:
      </label>
      <input
        type="number"
        min="0"
        className="border p-2"
        value={ticketCost}
        onChange={(e) => setTicketCost(e.target.value)}
        style={{ width: 120 }}
        required
      />
    </div>
    {/* Camera / Photo Buttons */}
<div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: "8px" }}>
  <Button
    type="button"
    variant="gray"
    onClick={() => {
      const el = document.getElementById("cameraPicker");
      if (el) el.click();
    }}
  >
    📷 Take Photo
  </Button>

  <Button
    type="button"
    variant="gray"
    onClick={() => {
      const el = document.getElementById("filePicker");
      if (el) el.click();
    }}
  >
    🖼️ Choose Photo
  </Button>
</div>

    {/* File Picker & Drag-and-Drop */}
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={async (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith("image/")) {
          setFileToUpload(file);
        }
      }}
      style={{
        border: `2px dashed ${isDragging ? "#2563eb" : "#cbd5e1"}`,
        borderRadius: 12,
        padding: "24px",
        textAlign: "center",
        background: isDragging ? "#eff6ff" : "#fafafa",
        color: "#334155",
        cursor: "pointer",
      }}
      onClick={() => document.getElementById("filePicker").click()}
    >
      {fileToUpload ? (
        <div>
          <p>📸 Selected: {fileToUpload.name}</p>
          <img
            src={URL.createObjectURL(fileToUpload)}
            alt="Preview"
            style={{
              maxWidth: "160px",
              marginTop: 8,
              borderRadius: 8,
            }}
          />
        </div>
      ) : (
        <p>
          Drag & drop an image here, or <strong>click to browse</strong>
        </p>
      )}
      <input
  id="filePicker"
  type="file"
  accept="image/*"
  style={{ display: "none" }}
  onChange={(e) => {
    const f = e.target.files?.[0] || null;
    setFileToUpload(f);
  }}
/>
<input
  id="cameraPicker"
  type="file"
  accept="image/*"
  capture="environment"
  style={{ display: "none" }}
  onChange={(e) => setFileToUpload(e.target.files[0] || null)}
/>
    </div>

    {/* Submit Button */}
    <Button
      type="submit"
      variant="green"
      disabled={uploadBusy || !name.trim() || !fileToUpload}
    >
      {uploadBusy ? "Uploading..." : "➕ Add Item"}
    </Button>

    {successMessage && (
      <p style={{ color: "green", marginTop: "0.5rem" }}>{successMessage}</p>
    )}
  </form>
</div>
  
        {/* Search */}
        <div className="section-card" style={{ marginTop: "1rem" }}>
          <input
            className="border p-2 w-full"
            placeholder="Search items…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
  
        {/* Grid */}
        <div className="section-card" style={{ marginTop: "1rem" }}>
          <h3 style={{ marginBottom: "0.75rem" }}>Items</h3>
  
          {loading ? (
            <p>Loading…</p>
          ) : filtered.length === 0 ? (
            <p>No items found.</p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                gap: "12px",
              }}
            >
              {filtered.map((item) => {
                const cost = item.ticketCost ?? item.cost ?? 0;
                const qrValue = itemQR(item.id);
                const isChecked = !!selected[item.id];
  
                return (
                  <div
                    key={item.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      overflow: "hidden",
                      background: "#fff",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      style={{
                        aspectRatio: "1.5 / 1",
                        background: "#f9fafb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleSelect(item.id)}
                        title="Select for printing"
                        style={{ position: "absolute", top: 8, left: 8, width: 18, height: 18 }}
                      />
                      {item.imageUrl ? (
  <img
    src={item.imageUrl}
    alt={item.name}
    loading="lazy"
    style={{
      width: "100%",
      height: "160px",
      objectFit: "cover",
      display: "block"
    }}
  />
) : (
  <span style={{ color: "#9ca3af" }}>No image</span>
)}
                    </div>
  
                    <div style={{ padding: "0.75rem", display: "grid", gap: "0.35rem" }}>
                      <div style={{ fontWeight: 600 }}>{item.name}</div>
                      <div style={{ fontSize: 14, color: "#374151" }}>
                        Cost: <strong>{cost}</strong> tickets
                      </div>
                      <div style={{ fontSize: 12, color: item.available ? "#16a34a" : "#ef4444" }}>
                        {item.available ? "Available" : "Unavailable"}
                      </div>
  
                      <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.75rem", alignItems: "center" }}>
                        <QRCodeSVG value={qrValue} size={96} includeMargin />
                        <div style={{ fontSize: 11, color: "#6b7280" }}>
                          Scan in Store Mode
                          <div style={{ fontSize: 10, marginTop: 2, wordBreak: "break-all" }}>
                            {qrValue}
                          </div>
                        </div>
                      </div>
  
                      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
                        <Button variant="primary" onClick={() => toggleAvailable(item.id, item.available)}>
                          {item.available ? "Mark Unavailable" : "Mark Available"}
                        </Button>
                        <Button variant="red" onClick={() => handleDelete(item.id)}>
                          Delete
                        </Button>
                        <Button
                          variant="indigo"
                          onClick={() => openPrintSheet([item.id])}
                          title="Print this item's QR"
                        >
                          🖨️ Print Label
                        </Button>
                      </div>
  
                      <div style={{ fontSize: 11, color: "#6b7280", marginTop: "0.5rem" }}>
                        ID: {item.id}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
  
                {/* PRINT OVERLAY: portaled outside #root so print excludes the
                    full store-management page and its QR/image-heavy item grid. */}
                {printOpen && createPortal(
          <>
            <div
              id="store-label-print-sheet"
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
                padding: 16,
              }}
            >
              <div
                style={{
                  width: "100%",
                  maxWidth: 900,
                  maxHeight: "90vh",
                  background: "white",
                  borderRadius: 12,
                  overflow: "auto",
                  padding: 24,
                }}
              >
                {/* Controls */}
                <div
                  className="print-hide"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                  }}
                >
                  <h3 style={{ margin: 0 }}>Print Labels</h3>
                  <div style={{ display: "flex", gap: 8 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                      <input
                        type="checkbox"
                        checked={includePrintImages}
                        onChange={(event) => setIncludePrintImages(event.target.checked)}
                      />
                      Include images
                    </label>
                    <Button variant="outline" onClick={() => window.print()}>
                      🖨️ Print
                    </Button>
                    <Button variant="gray" onClick={closePrintSheet}>
                      Close
                    </Button>
                  </div>
                </div>

                {/* Label Grid */}
                <div className="print-grid">
                  {printIds
                    .map((id) => items.find((i) => i.id === id))
                    .filter(Boolean)
                    .map((item) => {
                      const qrValue = itemQR(item.id);

                      return (
                        <div key={item.id} className="print-card">
                          <div className="label-title">{item.name}</div>
                          <div style={{ fontSize: "14px", marginBottom: "6px" }}>
                            🎟 {item.ticketCost ?? item.cost ?? 0} tickets
                          </div>
                          {includePrintImages && item.imageUrl && (
                            <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="label-image"
                            loading="eager"
                            style={{
                              maxWidth: "180px",
                              maxHeight: "120px",
                              objectFit: "contain"
                            }}
                          />
                          )}

                          <QRCodeSVG
                            value={qrValue}
                            size={120}
                            includeMargin
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>

            {/* PRINT STYLES */}
            <style>{`
              @media print {
                #root {
                  display: none !important;
                }

                #store-label-print-sheet {
                  position: static !important;
                  display: block !important;
                  background: white !important;
                  padding: 0 !important;
                }

                #store-label-print-sheet > div {
                  width: auto !important;
                  max-width: none !important;
                  max-height: none !important;
                  overflow: visible !important;
                  border-radius: 0 !important;
                  padding: 0 !important;
                }

                .print-hide {
                  display: none !important;
                }

                @page {
                  margin: 0.5in;
                }
              }

              .print-grid {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                gap: 20px;
              }

              .print-card {
                border: 1px solid #333;
                padding: 12px;
                text-align: center;
                page-break-inside: avoid;
              }

              .label-title {
                font-weight: bold;
                margin-bottom: 8px;
                font-size: 16px;
              }

              .label-image {
                width: 100%;
                max-height: 120px;
                object-fit: contain;
                margin-bottom: 8px;
              }
            `}</style>
          </>
          , document.body
        )}
      </div>
    );
};

export default StoreAdmin;
